import React, { useEffect, useState } from "react";
import "./style.css";
import { Collapse, message, Skeleton } from "antd";
import { getDecisionEngine, getRecommendation } from "../../../API/decisionEngine";
import Lottie from "lottie-react";
import reportGeneration from "../../../Assets/Animations/Report Generation.json";
import aiThinking from "../../../Assets/Animations/AiThinking.json";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import AreaChart from "../../../Components/Charts/AreaChart";
import RecommendedFuelPumps from "./RecommededFuelPumps";

const { Panel } = Collapse;

const SkeletonLoader = () => {
  return (
    Array.from({ length: 5 }).map((_, idx) => (
      <div key={idx} className="pumps-list">
        <Skeleton.Avatar active size="large" shape="circle" />
        <div style={{ flex: 1, marginLeft: "12px" }}>
          <Skeleton.Input style={{ width: "80%" }} active size="small" />
          <Skeleton.Input style={{ width: "60%", marginTop: "8px" }} active size="small" />
        </div>
        <Skeleton.Button active size="small" style={{ width: "60px" }} />
      </div>
    ))
  )
}
const DecisionEngine = () => {
  const { from, to } = useDateFilter();
  const [decData, setDecData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recLoading, setRecLoading] = useState(false);
  const [isPumpLoading, setIsPumpLoading] = useState(false);

  const [startValue, setStartValue] = useState("");
  const [endValue, setEndValue] = useState("");
  const [pumps, setPumps] = useState([])
  const [isPumpSuggested, setIsPumpSuggested] = useState(false);

  const [recommendations, setRecommendations] = useState();
  const [selectedPump, setSelectedPump] = useState(null);

  useEffect(() => {
    if (!to || !from) {
      return;
    }

    const fetchDecison = async () => {
      try {
        setLoading(true);
        const response = await getDecisionEngine(to, from);
        await setDecData(response.data);
        setLoading(false);
      } catch (error) {
        message.error("Failed to fetch fuel data");
        console.log(error, "Failed to fetch fuel data");
      }
      setLoading(false);
    };

    const fetchRecommendations = async () => {
      try {
        setRecLoading(true);
        const recommendation = await getRecommendation(to, from)
        setRecommendations(recommendation?.data)
        setRecLoading(false);
      } catch (error) {
        message.error("Failed to fetch fuel data");
        console.log(error, "Failed to fetch fuel data");
      }
      setRecLoading(false);
    };

    fetchDecison();
    fetchRecommendations();
  }, [to, from]);

  const handlePumpSelection = (pump) => {
    setSelectedPump(pump)
  }

  return (
    <div className="decision-engine">
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <Lottie
            animationData={reportGeneration}
            loop={true}
            style={{ width: 150, height: 150 }}
          />
        </div>
      ) : (
        <>
          <div className="rows-container">
            <div className="row tabs">
              <div className="tab-item">
                <div className="left">
                  <span className="tab-title">Vol of Fuel Added</span>
                  <span className="value-box" style={{ color: "#1FC16B" }}>
                    {decData?.fuel_added} L
                  </span>
                </div>
              </div>

              <div className="tab-item">
                <div className="left">
                  <span className="tab-title">Cost of Fuel</span>
                  <span className="value-box" style={{ color: "#DFB400" }}>
                    $ {decData?.cost_of_fuel}
                  </span>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="graph">
                <AreaChart
                  graphData={decData?.volume_fuel_used || {}}
                  length={5}
                  colourTheme={["#DFB400", "#B73AFF"]}
                  graphTitle="Volume of Fuel Used"
                  selectTenByDefault={true}
                  units={["Hr", "$"]}
                  classNameCSS="idle-movement"
                />
              </div>
            </div>

            <div className="row">
              <div className="map-component-container">
                <div className="title">Recommended Pumps on the Route</div>
                <div className="map-component">
                  <div className="map-route">
                    <RecommendedFuelPumps
                      vehicles={decData?.vehicles_list}
                      from={from}
                      to={to}
                      startValue={startValue}
                      endValue={endValue}
                      setStartValue={setStartValue}
                      setEndValue={setEndValue}
                      setPumps={setPumps}
                      selectedPump={selectedPump}
                      setIsPumpSuggested={setIsPumpSuggested}
                      setIsPumpLoading={setIsPumpLoading}
                    />
                  </div>
                  {(pumps.length > 0 || isPumpLoading) && (
                    <div className="pumps-list-container">
                      {isPumpLoading ? (
                        <SkeletonLoader />
                      ): isPumpSuggested ? pumps.map((pump, index) => (
                      <div
                        key={index}
                        className="pumps-list"
                        onClick={() => handlePumpSelection(pump)}
                      >
                        <div className="pump-details">
                          <span className="pump-name">{pump?.station}</span>
                          <span className="pump-address">{pump?.fuel_type}</span>
                        </div>
                        <div className="fuel-price">
                          <span>{pump?.distance_km} KM</span>
                          <div className="pump-address">${pump?.price}</div>
                        </div>
                      </div>
                      )) : <div className="pump-details">
                        <span className="pump-name">{pumps}</span>
                      </div>}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="map-component-container">
                <div className="title">Recommendations by Vehicle</div>
                {recLoading ?
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    className="loader-container"
                  >
                    <Lottie
                      animationData={aiThinking}
                      loop={true}
                      style={{ width: 150, height: 150 }}
                    />
                  </div> :
                  recommendations && (
                    <Collapse accordion>
                      {Object.entries(recommendations).map(([key, recs]) => (
                        <Panel header={recs?.statement || key} key={key}>
                          <ul className="recommendation-list">
                            {recs.suggestions.map((rec, idx) => (
                              <li key={idx}>{rec}</li>
                            ))}
                          </ul>
                        </Panel>
                      ))}
                    </Collapse>
                  )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default DecisionEngine;
